// Declarative data-attribute animation engine (guidelines §5).
// Any template opts in with data-anim="fade-up|fade|scale-in|line|parallax|
// clip-reveal|hero-drift" or data-anim-group + data-anim-item, plus the
// data-anim-from / data-anim-delay / data-speed modifiers.
import { gsap, prefersReduced, PREMIUM_EASE, REVEAL_DISTANCE, ScrollTrigger } from '../gsap-init.js';

function buildReveals(heavy) {
  const root = document.querySelector('.site-main') || document.body;
  const q = (sel) => gsap.utils.toArray(root.querySelectorAll(sel));

  const fromVars = (el) => {
    const dir = el.dataset.animFrom;
    if (dir === 'left') return { x: -REVEAL_DISTANCE, y: 0 };
    if (dir === 'right') return { x: REVEAL_DISTANCE, y: 0 };
    if (dir === 'down') return { x: 0, y: -REVEAL_DISTANCE };
    return { x: 0, y: REVEAL_DISTANCE }; // "up" default
  };

  const revealOnce = (el) => {
    gsap.set(el, { autoAlpha: 0, ...fromVars(el) });
    gsap.to(el, {
      autoAlpha: 1,
      x: 0,
      y: 0,
      duration: 0.95,
      delay: Number(el.dataset.animDelay) || 0,
      ease: PREMIUM_EASE,
      scrollTrigger: { trigger: el, start: 'top 88%', once: true },
    });
  };

  // fade-up / fade
  q('[data-anim="fade-up"], [data-anim="fade"]')
    .filter((el) => !el.closest('[data-anim-group]'))
    .forEach(revealOnce);

  // scale-in
  q('[data-anim="scale-in"]').forEach((el) => {
    gsap.set(el, { autoAlpha: 0, scale: 0.94 });
    gsap.to(el, {
      autoAlpha: 1,
      scale: 1,
      duration: 1,
      delay: Number(el.dataset.animDelay) || 0,
      ease: PREMIUM_EASE,
      scrollTrigger: { trigger: el, start: 'top 88%', once: true },
    });
  });

  // stagger groups
  q('[data-anim-group]').forEach((group) => {
    const items = gsap.utils.toArray(group.querySelectorAll('[data-anim-item]'));
    if (!items.length) return;
    gsap.set(items, { autoAlpha: 0, ...fromVars(items[0]) });
    gsap.to(items, {
      autoAlpha: 1,
      x: 0,
      y: 0,
      duration: 0.9,
      ease: PREMIUM_EASE,
      stagger: 0.1,
      scrollTrigger: { trigger: group, start: 'top 82%', once: true },
    });
  });

  // hairline dividers
  q('[data-anim="line"]').forEach((el) => {
    gsap.set(el, { transformOrigin: 'left center', scaleX: 0 });
    gsap.to(el, {
      scaleX: 1,
      duration: 1.1,
      ease: 'power2.inOut',
      scrollTrigger: { trigger: el, start: 'top 90%', once: true },
    });
  });

  // slide-in: decorative elements scrub in from the screen edge as their
  // section approaches (data-anim-from="left|right"). Transform-only, so it's
  // cheap enough to run on mobile too.
  q('[data-anim="slide-in"]').forEach((el) => {
    const fromRight = el.dataset.animFrom === 'right';
    const offscreenX = () => {
      const pad = 32;
      if (fromRight) {
        const parent = el.offsetParent || el.parentElement;
        return (parent ? parent.clientWidth : window.innerWidth) - el.offsetLeft + pad;
      }
      return -(el.offsetLeft + el.offsetWidth + pad);
    };
    gsap.fromTo(
      el,
      { x: offscreenX },
      {
        x: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: el.parentElement || el,
          start: 'top 95%',
          end: 'top 45%',
          scrub: 1,
          invalidateOnRefresh: true,
        },
      },
    );
  });

  if (!heavy) return; // mobile stops here

  // parallax (desktop only)
  q('[data-anim="parallax"]').forEach((el) => {
    const speed = parseFloat(el.dataset.speed || '0.12');
    gsap.fromTo(
      el,
      { yPercent: -speed * 50 },
      {
        yPercent: speed * 50,
        ease: 'none',
        scrollTrigger: {
          trigger: el.parentElement || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      },
    );
  });

  // hero drift (desktop only)
  q('[data-anim="hero-drift"]').forEach((el) => {
    gsap.to(el, {
      yPercent: -18,
      opacity: 0.25,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });
  });

  // clip reveal for images (desktop only)
  q('[data-anim="clip-reveal"]').forEach((el) => {
    gsap.fromTo(
      el,
      { clipPath: 'inset(12% 6% 12% 6% round 2px)' },
      {
        clipPath: 'inset(0% 0% 0% 0% round 0px)',
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top 92%',
          end: 'top 42%',
          scrub: true,
        },
      },
    );
  });
}

// Layout shift is the #1 cause of broken trigger positions (guidelines §9).
function scheduleRefreshes() {
  const refresh = () => ScrollTrigger.refresh();

  window.addEventListener('load', refresh);

  // Staggered timeouts cover: JS paint, web font swap, image load, preloader exit.
  setTimeout(refresh, 400);
  setTimeout(refresh, 1200);
  setTimeout(refresh, 3400);

  if ('fonts' in document) {
    document.fonts.ready.then(refresh).catch(() => {});
  }
}

if (!prefersReduced) {
  const mm = gsap.matchMedia();
  mm.add('(min-width: 768px)', () => buildReveals(true)); // desktop: full
  mm.add('(max-width: 767px)', () => buildReveals(false)); // mobile: light
  scheduleRefreshes();
}
